// app/src/lib/procurement/priceUnit.js — unit-of-measure buckets + the
// suspicious-move guard shared by the price-movers widget and the Price-changes
// report.
//
// Two observations of the same product only compare when they share a unit
// bucket: a per-pound price ($1.92) and a per-case price ($40) of "Broccoli
// Crowns" are DIFFERENT series, not a 2,082% increase. When the unit is blank
// on both sides we fall back to the ± SUSPICIOUS_PCT guard, which also catches
// OCR misreads (a dropped decimal, a qty/price swap) that survive the keying.

// Canonical unit synonyms → one bucket. Anything unrecognized keeps its own
// stripped spelling (still a stable key), so unknown units never cross with lb.
const UOM_CANON = {
  lb: 'lb', lbs: 'lb', pound: 'lb', pounds: 'lb',
  oz: 'oz', ounce: 'oz', ounces: 'oz',
  kg: 'kg', kgs: 'kg', kilo: 'kg', kilos: 'kg', kilogram: 'kg', kilograms: 'kg',
  g: 'g', gr: 'g', gram: 'g', grams: 'g',
  cs: 'cs', case: 'cs', cases: 'cs',
  bg: 'bg', bag: 'bg', bags: 'bg',
  bx: 'bx', box: 'bx', boxes: 'bx',
  ct: 'ct', cnt: 'ct', count: 'ct',
  ea: 'ea', each: 'ea', unit: 'ea', units: 'ea', pc: 'ea', pcs: 'ea', piece: 'ea', pieces: 'ea',
  pk: 'pk', pkg: 'pk', pack: 'pk', packs: 'pk', package: 'pk',
  doz: 'doz', dz: 'doz', dozen: 'doz',
  gal: 'gal', gallon: 'gal', gallons: 'gal',
  qt: 'qt', quart: 'qt', quarts: 'qt',
  pt: 'pt', pint: 'pt', pints: 'pt',
  l: 'l', ltr: 'l', liter: 'l', litre: 'l', liters: 'l', litres: 'l',
  ml: 'ml',
};

/** Normalize a unit of measure to a comparison bucket ('' when unknown/blank). */
export function normUom(uom) {
  const s = String(uom || '').toLowerCase().replace(/[^a-z]/g, '');
  if (!s) return '';
  return UOM_CANON[s] || s;
}

/**
 * Given the unit codes seen for ONE product at ONE vendor, decide whether its
 * price series must be split by unit. Split ONLY when the vendor genuinely sold
 * it under two or more DIFFERENT units (per-lb AND per-case) — a per-lb price
 * must never compare against a per-case price. When there's at most one distinct
 * populated unit, a blank unit is just missing OCR data for that single real
 * unit; keeping it in its own bucket would fragment a real series and hide a
 * legitimate price move, so we DON'T split.
 */
export function unitsConflict(uoms) {
  const distinct = new Set();
  for (const u of uoms) { const n = normUom(u); if (n) distinct.add(n); }
  return distinct.size >= 2;
}

// Owner rule (2026-07): a swing over ±50% is almost always a unit/pack mismatch
// or an OCR misread, not a real price change. Such moves are held for review —
// never shown as a headline "up 2,082%".
export const SUSPICIOUS_PCT = 50;

/** True when an old→new move is too large to trust as a real price change. */
export function isSuspiciousMove(oldCents, newCents) {
  if (!(oldCents > 0) || !(newCents > 0)) return true; // zero/negative price → can't trust
  const pct = Math.abs((newCents - oldCents) / oldCents) * 100;
  return pct > SUSPICIOUS_PCT;
}

/**
 * A blank-unit series, at a vendor that sold this item under 2+ real units:
 * are its OWN points actually a mix, or one coherent unit whose label the OCR
 * just never read?
 *
 * The conservative answer used to be "always a mix", which held perfectly good
 * series for review — King's Oriental billed CANOLA Oil 35# at 35.15, 36.70,
 * 36.70, 36.70, 41.55 with the unit blank every time, and a +13% move on that
 * was parked next to genuine 1,600% unit mismatches (owner report 2026-08-11).
 *
 * A series that tight is like-for-like whatever the missing label says, so
 * comparing inside it is safe. Only a series whose own prices straddle more
 * than one magnitude band — a blank $3/lb sitting next to a blank $40/case —
 * can produce a comparison that silently crosses units. Same ± SUSPICIOUS_PCT
 * threshold used everywhere else, applied to the series spread.
 *
 * @param {number[]} centsList unit prices of the blank series (excluded points already removed)
 * @returns {boolean} true when the series really could be mixed units
 */
export function blankSeriesIsAmbiguous(centsList) {
  const cents = (centsList || []).filter((c) => c > 0);
  if (cents.length < 2) return false;
  return Math.max(...cents) / Math.min(...cents) > 1 + SUSPICIOUS_PCT / 100;
}
